import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { formatDate, pluralise, relativeFromNow } from '@/lib/format'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'

/**
 * Upper bound for the date picker: nothing is prepared in the future, and a
 * future date would sit the recipe outside the eligibility window for two
 * months. Built from the local calendar day, not UTC, to match daysSince.
 */
const NO_FUTURE_DAY = (() => {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
})()

/**
 * The list of dates a recipe was prepared on, editable in place.
 *
 * The count is whatever this list adds up to and "last prepared" is its final
 * entry, so there is no separate field to keep in step with anything. Adding a
 * date is how a forgotten button press gets corrected; removing one is the
 * undo for a mistaken record. Shared by the recipe form and the detail dialog.
 */
export function PreparationEditor({
  dates,
  onChange,
  className,
}: {
  dates: string[]
  onChange: (dates: string[]) => void
  className?: string
}) {
  const [entry, setEntry] = useState('')

  const sorted = [...dates].sort()
  const latest = sorted.at(-1) ?? null

  function add() {
    // Adding a day already on record is a no-op rather than a second entry,
    // matching what the endpoint does.
    if (entry && !sorted.includes(entry)) onChange([...sorted, entry])
    setEntry('')
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-label font-medium text-ink-soft">Preparations</span>
        <span className="tabular text-sm text-ink-faint">
          {sorted.length === 0
            ? 'never prepared'
            : `${pluralise(sorted.length, 'time')}, last ${relativeFromNow(latest)}`}
        </span>
      </div>

      <div className="mt-2 flex items-end gap-2">
        <Input
          label="Prepared on"
          type="date"
          wrapperClassName="flex-1"
          value={entry}
          onChange={(event) => setEntry(event.target.value)}
          max={NO_FUTURE_DAY}
          hint="Back-date a meal you forgot to log."
        />
        <Button variant="secondary" onClick={add} disabled={!entry}>
          <Plus size={14} aria-hidden />
          Add
        </Button>
      </div>

      {sorted.length === 0 ? (
        <p className="mt-2 rounded-[var(--radius-field)] border border-dashed border-line-strong px-3 py-4 text-center text-sm text-pretty text-ink-faint">
          No preparations recorded.
        </p>
      ) : (
        <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto scroll-slim">
          {sorted.map((date) => (
            <li
              key={date}
              className="flex items-center justify-between gap-2 rounded-[var(--radius-field)] bg-surface-sunken px-3 py-1.5"
            >
              <span className="tabular text-sm text-ink">
                {formatDate(date)}
                <span className="ml-2 text-xs text-ink-faint">
                  {relativeFromNow(date)}
                </span>
              </span>
              <button
                type="button"
                aria-label={`Remove the preparation on ${formatDate(date)}`}
                onClick={() => onChange(sorted.filter((item) => item !== date))}
                className="shrink-0 rounded p-1 text-ink-faint hover:bg-surface hover:text-critical"
              >
                <X size={13} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}